/**
 * TanStack Query hooks.
 *
 * Refetch intervals are tuned to the measured 180s reporting cadence. Polling faster than
 * the data changes burns requests for nothing; polling much slower makes an operator
 * distrust the screen. 60s gives roughly three chances to catch each new payload without
 * hammering the API.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useState } from 'react';
import { pollInterval } from './backoff';
import { api, ApiError } from './client';
import type {
  AlarmLifecycleAction,
  AlarmLifecycleState,
  AlarmTriage,
  Aggregation,
  AlarmListResponse,
  AlarmSummaryResponse,
  BucketInterval,
  ComplianceHistoryResponse,
  ExposureResponse,
  IngestQualityResponse,
  HealthResponse,
  LatestResponse,
  MetaResponse,
  RangeResponse,
  RootCause,
  SeriesListResponse,
  SeriesRow,
  TheftInput,
} from './types';

/** Matches the publish cadence measured off the live broker. */
export const REPORTING_CADENCE_MS = 180_000;
const LIVE_REFETCH_MS = 60_000;

export const keys = {
  meta: ['meta'] as const,
  sites: (filters?: Record<string, unknown>) => ['sites', filters ?? {}] as const,
  health: (siteId?: string) => ['telemetry', 'health', siteId ?? 'all'] as const,
  series: (filters: Record<string, unknown>) => ['telemetry', 'series', filters] as const,
  latest: (filters: Record<string, unknown>) => ['telemetry', 'latest', filters] as const,
  range: (filters: Record<string, unknown>) => ['telemetry', 'range', filters] as const,
  alarms: (filters: Record<string, unknown>) => ['alarms', filters] as const,
  alarmSummary: (siteId?: string) => ['alarms', 'summary', siteId ?? 'all'] as const,
  alarmTriage: (id: string) => ['alarms', 'triage', id] as const,
  exposure: ['sites', 'exposure'] as const,
  complianceHistory: (siteId: string) => ['sites', siteId, 'compliance', 'history'] as const,
  ingestQuality: (siteId?: string, days?: number) =>
    ['telemetry', 'ingest-quality', siteId ?? 'all', days ?? 7] as const,
};

/**
 * A 4xx means the request itself is wrong, so retrying cannot help — it just delays the
 * error the user needs to see. 5xx and 429 are worth one retry.
 */
function retryPolicy(count: number, error: unknown): boolean {
  if (error instanceof ApiError && error.isClientFault) return false;
  return count < 2;
}

const base = { retry: retryPolicy } satisfies Partial<UseQueryOptions>;

export function useMeta() {
  return useQuery({
    ...base,
    queryKey: keys.meta,
    queryFn: () => api.get<MetaResponse>('/meta'),
    // Immutable per deployment; the engine digest only changes on deploy.
    staleTime: Infinity,
  });
}

/*
 * `useSites` REMOVED — it lives in `lib/api/sites.ts`.
 *
 * This one took a `filters` argument that all three of its callers left empty, and passed
 * no `limit`, so it silently took the server's default of 50 while the other hook asked for
 * 200. Two cache keys, two page sizes, one question: above fifty sites the gateway-assign
 * and feed-health pickers would have shown a shorter fleet than the header selector, with
 * nothing on screen to say so.
 *
 * If per-field filtering is ever wanted, add it to the surviving hook rather than reviving
 * this one.
 */

export function useSiteHealth(siteId?: string) {
  return useQuery({
    ...base,
    queryKey: keys.health(siteId),
    queryFn: () => api.get<HealthResponse>('/telemetry/health', siteId ? { siteId } : undefined),
    refetchInterval: pollInterval(LIVE_REFETCH_MS),
    staleTime: 30_000,
  });
}

/**
 * Fleet exposure by compliance domain.
 *
 * Fleet-wide by design — there is no `siteId` variant. The question this answers is
 * "which domain is worst ACROSS the estate", and a single-site version of it is the
 * Compliance matrix, which already exists and says more. Adding a site filter here would
 * put two screens on the same fact with different resolutions.
 *
 * Re-evaluated on the server from the live site rows, so it follows a parameter edit on
 * the next fetch rather than on the next evaluation snapshot.
 */
/**
 * A site's score history and what moved it. Site-scoped only — there is no fleet variant,
 * because "why did it move" is a question about one plant's parameters and one plant's
 * audit trail, and averaging that across sites answers nobody.
 */
export function useComplianceHistory(siteId: string | undefined) {
  return useQuery({
    ...base,
    queryKey: keys.complianceHistory(siteId ?? 'none'),
    queryFn: () => api.get<ComplianceHistoryResponse>(`/sites/${siteId}/compliance/history`),
    enabled: Boolean(siteId),
    staleTime: 60_000,
  });
}

export function useFleetExposure() {
  return useQuery({
    ...base,
    queryKey: keys.exposure,
    queryFn: () => api.get<ExposureResponse>('/sites/exposure'),
    staleTime: 60_000,
  });
}

/**
 * Ingest yield and data quality. Slow-moving by nature — these are multi-day totals, so a
 * 5-minute stale time rather than the live cadence the health hook uses.
 */
export function useIngestQuality(siteId?: string, days?: number) {
  return useQuery({
    ...base,
    queryKey: keys.ingestQuality(siteId, days),
    queryFn: () =>
      api.get<IngestQualityResponse>('/telemetry/ingest-quality', {
        ...(siteId ? { siteId } : {}),
        ...(days ? { days: String(days) } : {}),
      }),
    staleTime: 300_000,
  });
}

/*
 * NEITHER OF THE NEXT TWO CARRIES A `refetchInterval` ANY MORE. `useAlarmStream` in
 * `events.ts` invalidates the `alarms` key prefix once per burst of pushed events AND on every
 * reconnect, and that invalidate is by prefix — it reaches these two plus AlarmsView's
 * paginated query and every other filtered call site (site pages, Overview charts, the Digital
 * Twin, device detail) in one call. A fixed poll on top of that was pure waste. If the stream
 * is down, `enabled: false` there falls back to this hook's own `staleTime`/mount behaviour
 * rather than going stale forever — the push makes this fast, it was never the only thing
 * keeping it correct.
 */
export function useAlarmSummary(siteId?: string, region?: string) {
  return useQuery({
    ...base,
    queryKey: region ? [...keys.alarmSummary(siteId), region] : keys.alarmSummary(siteId),
    queryFn: () =>
      api.get<AlarmSummaryResponse>('/alarms/summary', {
        ...(siteId ? { siteId } : {}),
        ...(region ? { region } : {}),
      }),
  });
}

/**
 * Alarms by where their WORK stands (`GET /alarms/states`): the Alarms queue's tab counts, over
 * the whole reach or one site, open and cleared alike — an alarm nobody owns is in the queue
 * whether or not its condition has cleared. States with none are absent from the map.
 */
export function useAlarmStateCounts(siteId?: string) {
  return useQuery({
    ...base,
    queryKey: ['alarms', 'states', siteId ?? 'all'] as const,
    queryFn: () =>
      api.get<{ counts: Partial<Record<AlarmLifecycleState, number>> }>('/alarms/states', siteId ? { siteId } : undefined),
    select: (r) => r.counts,
  });
}

/** What `/alarms` filters on. The comma-separated ones are lists of keys (Lumen-BE `alarmQuerySchema`). */
export interface AlarmFilters {
  /** One alarm by id (Lumen-BE 0091) — a reminder's link. */
  id?: string;
  siteId?: string;
  /** One device's alarms. The API has always taken it; the Single Cabinet View is the first caller. */
  deviceId?: number;
  /** The DEVICE's axis: is the condition still reported. */
  state?: 'open' | 'closed' | 'all';
  severity?: string;
  /** ISO bounds — on `openedAt`, or on the alarm's whole interval with `window: 'active'`. */
  from?: string;
  to?: string;
  /** `opened` (default): opened inside `from`–`to`. `active`: active at any moment inside it. */
  window?: 'opened' | 'active';
  /** The site's region, without case (CRATUS 2.0 #18). */
  region?: string;
  /** The OPERATOR's axis, comma-separated lifecycle states: `open` is the unowned queue. */
  lifecycle?: string;
  /** `me`, `none`, or a person's id. */
  assignee?: string;
  /** Comma-separated root causes, e.g. the two theft causes. */
  rootCause?: string;
  /** `'true'`: one row per incident — an alarm a composite stands for is left out (Lumen-BE 0089). */
  incidents?: 'true';
  limit?: number;
}

export function useAlarms(filters: AlarmFilters = {}, enabled = true) {
  return useQuery({
    ...base,
    queryKey: keys.alarms(filters as Record<string, unknown>),
    queryFn: () => api.get<AlarmListResponse>('/alarms', filters as Record<string, string | number>),
    enabled,
  });
}

/**
 * Several controllers' alarms over one window, one request each — the Analysis screen's event
 * lanes. One request per device rather than one for the fleet, so the 500-row page is spent on
 * the cabinets being compared and a busy cabinet elsewhere cannot crowd them out.
 */
export function useDeviceAlarms(deviceIds: readonly number[], window: { from: string; to: string }, enabled = true) {
  return useQueries({
    queries: deviceIds.map((deviceId) => {
      const filters: AlarmFilters = { deviceId, state: 'all', window: 'active', from: window.from, to: window.to, limit: 500 };
      return {
        ...base,
        queryKey: keys.alarms(filters as Record<string, unknown>),
        queryFn: () => api.get<AlarmListResponse>('/alarms', filters as Record<string, string | number>),
        enabled,
        staleTime: 30_000,
        refetchInterval: pollInterval(LIVE_REFETCH_MS),
      };
    }),
  });
}

export function useSeriesCatalog(filters: {
  siteId?: string;
  deviceId?: number;
  liveOnly?: boolean;
  q?: string;
  limit?: number;
} = {}) {
  return useQuery({
    ...base,
    queryKey: keys.series(filters),
    queryFn: () => api.get<SeriesListResponse>('/telemetry/series', filters),
    // The catalog only changes when new hardware appears.
    staleTime: 5 * 60_000,
  });
}

export function useLatest(
  filters: { siteId?: string; deviceId?: number; metrics?: string; limit?: number } = {},
  enabled = true,
) {
  return useQuery({
    ...base,
    queryKey: keys.latest(filters),
    queryFn: () => api.get<LatestResponse>('/telemetry/latest', filters),
    refetchInterval: pollInterval(LIVE_REFETCH_MS),
    enabled,
  });
}

/**
 * Some devices' current values, one request per device — the Analysis screen's controllers. One
 * fleet-wide request with a metric filter is cut by its row limit once the fleet is large (fifteen
 * readings a controller, ordered by device name), and the cabinets cut off would read as never
 * having reported. Per device, each answer is whole, and each shares its cache entry with any
 * other screen asking for that device's same metrics.
 */
export function useDeviceLatest(deviceIds: readonly number[], metrics: string, enabled = true) {
  return useQueries({
    queries: deviceIds.map((deviceId) => {
      const filters = { deviceId, metrics, limit: 200 };
      return {
        ...base,
        queryKey: keys.latest(filters),
        queryFn: () => api.get<LatestResponse>('/telemetry/latest', filters),
        refetchInterval: pollInterval(LIVE_REFETCH_MS),
        enabled,
      };
    }),
  });
}

export function useRange(
  params: {
    seriesIds: number[];
    from: string;
    to: string;
    bucket?: string;
    agg?: string;
  },
  enabled = true,
) {
  const query = {
    seriesIds: params.seriesIds.join(','),
    from: params.from,
    to: params.to,
    ...(params.bucket ? { bucket: params.bucket } : {}),
    ...(params.agg ? { agg: params.agg } : {}),
  };
  return useQuery({
    ...base,
    queryKey: keys.range(query),
    queryFn: () => api.get<RangeResponse>('/telemetry/range', query),
    enabled: enabled && params.seriesIds.length > 0,
    staleTime: 60_000,
  });
}

/* ================================ range cost contract (mirror of the server) */

/**
 * MIRROR of `Backend/lumen-be/src/modules/telemetry/schema.ts`. If that file changes,
 * this changes with it.
 *
 * It lives in the api layer rather than in a screen because THREE callers now need the
 * identical arithmetic and must never disagree: the batch size `useChunkedRange` splits a
 * selection on, the cost readout on the telemetry screen, and the bucket control that
 * decides which intervals are offerable at all. Two independent copies of this arithmetic
 * is exactly how a batch gets sized one series too large and a 553-series selection dies
 * on a 422 that the operator can do nothing about.
 */
export const MAX_BUCKETS = 5_000;
/** Hard per-request ceiling from `rangeQuerySchema` — a Zod `.max()`, not a cost estimate. */
export const MAX_SERIES_PER_QUERY = 50;
export const MAX_RAW_ROWS = 250_000;
/** The measured publish cadence. The server's row estimate is derived from this same 180. */
export const CADENCE_SECONDS = 180;

export const BUCKET_SECONDS: Record<BucketInterval, number> = {
  '3 minutes': 180,
  '15 minutes': 900,
  '1 hour': 3_600,
  '6 hours': 21_600,
  '1 day': 86_400,
  '7 days': 604_800,
};

export function bucketsFor(windowSeconds: number, bucket: BucketInterval): number {
  return Math.ceil(windowSeconds / BUCKET_SECONDS[bucket]);
}

/**
 * Raw rows one request will scan.
 *
 * The server computes `ceil((windowSeconds / 180) * seriesCount)`; this ceils the
 * per-series term FIRST, so it can only ever over-estimate. Deliberate: a client mirror
 * that under-estimates by one row hands the operator a 422, and being one row pessimistic
 * costs nothing.
 */
export function rawRowsFor(windowSeconds: number, seriesCount: number): number {
  return Math.ceil(windowSeconds / CADENCE_SECONDS) * seriesCount;
}

/**
 * Series per request for this window — i.e. the batch size.
 *
 * THE PROPERTY THAT MATTERS: `estimatedRawRows` does not depend on `bucket`, so coarsening
 * the bucket does nothing for a row breach and the two remedies stay disjoint. Splitting
 * the selection is the ONLY remedy that keeps every series.
 *
 * The `max(1, …)` clamp: a window long enough that even a single series breaches the row
 * budget (>12.5 years at the measured cadence) has no batch size that fits. Sending one
 * series and letting the server refuse it is the honest outcome — silently returning 0
 * would produce an empty chart with no error.
 */
export function maxSeriesPerRequest(windowSeconds: number): number {
  const perSeries = Math.ceil(windowSeconds / CADENCE_SECONDS);
  return Math.max(1, Math.min(MAX_SERIES_PER_QUERY, Math.floor(MAX_RAW_ROWS / perSeries)));
}

/** True when the metric has ever carried a value — i.e. the site really polls it. */
export function isPolled(row: SeriesRow): boolean {
  return row.everHadValue;
}

/* ==================================================== chunked range fetching */

/**
 * Concurrency for the batch pool.
 *
 * 4 rather than "all of them": a 545-series selection over 30 days is 33 requests, and 33
 * concurrent raw-scan queries against a hypertable with no rollups is the exact stall the
 * cost guard exists to prevent — splitting the work and then firing it all at once would
 * respect the letter of the guard and defeat its purpose. 4 also sits under the browser's
 * 6-per-host HTTP/1.1 limit, so the other calls on the page (health, alarms, catalog) are
 * not queued behind the batch run.
 */
const RANGE_CONCURRENCY = 4;

/** Per-batch attempts. One retry covers a transient blip without re-running 33 requests. */
const BATCH_ATTEMPTS = 2;
const BATCH_RETRY_MS = 400;

export interface ChunkedRangeProgress {
  /** Requests that have settled — resolved or given up on. */
  requestsDone: number;
  requestsTotal: number;
  /** Series whose request has come back. NOT the count that carried data. */
  seriesDone: number;
  seriesTotal: number;
}

export interface ChunkedRangeResult {
  /** The batches merged into one response, ordered to match the requested id order. */
  response: RangeResponse;
  requestsOk: number;
  requestsTotal: number;
  /**
   * Series whose batch failed after retries, so they are absent from `response`.
   *
   * Reported rather than thrown: losing 17 of 545 series to one flaky request should not
   * blank a chart that has 528 good ones on it — but it must not look like 528 was all
   * that was asked for either.
   */
  failedSeriesIds: number[];
  /** Series per request for this window. */
  batchSize: number;
  /**
   * The ids this result ANSWERS, in the order they were requested.
   *
   * Carried on the result because `placeholderData: (prev) => prev` below hands the
   * PREVIOUS selection's response to the new selection's cache slot for the whole duration
   * of a sweep — 11 requests at 24 h, 33 at 30 d. A caller that diffs "which requested ids
   * came back empty" against that placeholder concludes that almost everything is unknown.
   * That is not hypothetical: it shipped, and it rendered a "these series returned nothing
   * — drop them" action over a still-running sweep, which took a 545-series selection back
   * to 1 in one click. `isPlaceholderData` says the same thing, but this makes the
   * correspondence checkable from the DATA rather than from a flag, so the check survives
   * anyone changing the key or the placeholder strategy.
   */
  requestedSeriesIds: number[];
}

export function chunkIds(ids: readonly number[], size: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push([...ids.slice(i, i + size)]);
  return out;
}

/**
 * Merge batch responses into one.
 *
 * `cost` is summed, not averaged: the scan the database actually performed is the sum of
 * the batches, and reporting one batch's cost as the query's cost would tell the operator
 * a 545-series sweep was as cheap as a 50-series one.
 */
export function mergeRangeResponses(
  parts: readonly RangeResponse[],
  order: readonly number[],
): RangeResponse {
  const first = parts[0];
  if (!first) throw new Error('mergeRangeResponses: no parts');
  const rank = new Map(order.map((id, i) => [id, i]));
  return {
    bucket: first.bucket,
    agg: first.agg,
    from: first.from,
    to: first.to,
    source: first.source,
    cost: {
      // Buckets is per-request and identical across batches (same window, same bucket).
      buckets: first.cost.buckets,
      series: parts.reduce((n, p) => n + p.cost.series, 0),
      estimatedRawRows: parts.reduce((n, p) => n + p.cost.estimatedRawRows, 0),
    },
    // Requested order, so series colour and CSV column order do not shuffle when batches
    // come back out of order.
    series: parts
      .flatMap((p) => p.series)
      .sort((a, b) => (rank.get(a.seriesId) ?? 0) - (rank.get(b.seriesId) ?? 0)),
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Bounded-concurrency map that stops pulling work as soon as one item fails fatally. */
async function inPool<T>(
  items: readonly T[],
  limit: number,
  signal: AbortSignal | undefined,
  work: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  let fatal: unknown = null;
  const worker = async (): Promise<void> => {
    for (;;) {
      if (fatal !== null || signal?.aborted) return;
      const i = next++;
      const item = items[i];
      if (item === undefined) return;
      try {
        await work(item);
      } catch (e) {
        // Stop the stampede: without this the other workers keep issuing requests for a
        // run whose result is already being thrown away.
        fatal = e;
        return;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  if (fatal !== null) throw fatal;
}

async function fetchRangeBatch(
  ids: readonly number[],
  rest: { from: string; to: string; bucket: BucketInterval; agg: Aggregation },
  signal: AbortSignal | undefined,
): Promise<RangeResponse> {
  const query = { seriesIds: ids.join(','), ...rest };
  let last: unknown = null;
  for (let attempt = 0; attempt < BATCH_ATTEMPTS; attempt++) {
    try {
      return await api.get<RangeResponse>('/telemetry/range', query, signal);
    } catch (e) {
      last = e;
      // A 4xx means the request itself is wrong and every input is identical on a retry.
      // For the cost guard specifically it also means this mirror has drifted from the
      // server, which must surface immediately rather than after a backoff.
      if (e instanceof ApiError && e.isClientFault) throw e;
      if (signal?.aborted) throw e;
      if (attempt < BATCH_ATTEMPTS - 1) await sleep(BATCH_RETRY_MS);
    }
  }
  throw last;
}

/**
 * One range query over ARBITRARILY MANY series, by splitting it into requests the cost
 * guard accepts and merging the answers.
 *
 * WHY THIS EXISTS: `/telemetry/range` refuses a request whose `seriesIds` exceed 50 or
 * whose window x series exceeds 250,000 raw rows. Both limits are per REQUEST, not per
 * question — so "chart everything this site polls" (545 series at JAZZMSC5) is not an
 * expensive query, it is 11 cheap ones at 24 h and 33 at 30 d. The old UI answered it by
 * capping the selection at 50 and telling the operator the other 495 were unavailable.
 *
 * The batch size comes from `maxSeriesPerRequest`, which is the server's own arithmetic,
 * so a batch is never rejected. If one ever is, `fetchRangeBatch` throws it straight
 * through instead of retrying — a cost 422 here is a drift bug in the mirror above, and
 * hiding it behind a retry would make it undiagnosable.
 *
 * Failures are NOT all-or-nothing. A 5xx or a dropped connection on one batch costs that
 * batch's series and is reported in `failedSeriesIds`; only a 4xx or a total wipeout
 * rejects the query.
 */
export function useChunkedRange(
  params: {
    seriesIds: number[];
    from: string;
    to: string;
    bucket: BucketInterval;
    agg: Aggregation;
  },
  opts: {
    enabled: boolean;
    refetchMs: number | false;
    focusRefetch: boolean;
    staleMs: number;
    concurrency?: number | undefined;
  },
): { query: UseQueryResult<ChunkedRangeResult, Error>; progress: ChunkedRangeProgress | null } {
  const [progress, setProgress] = useState<ChunkedRangeProgress | null>(null);

  const windowSeconds = (Date.parse(params.to) - Date.parse(params.from)) / 1000;
  const batchSize = maxSeriesPerRequest(windowSeconds);
  const ids = params.seriesIds;
  const rest = { from: params.from, to: params.to, bucket: params.bucket, agg: params.agg };
  const concurrency = opts.concurrency ?? RANGE_CONCURRENCY;

  /*
   * The key is the WHOLE selection, not one per batch.
   *
   * Deliberate: `seriesIds` is what identifies the question, the merged result is what the
   * chart consumes, and one cache entry means one `isFetching`, one `dataUpdatedAt` and one
   * refetch timer. Caching batches individually would let a one-series change reuse most
   * of them, but batch boundaries shift when the selection changes, so the reuse is mostly
   * illusory and the bookkeeping is not.
   */
  const query = useQuery<ChunkedRangeResult, Error>({
    queryKey: keys.range({ ...rest, seriesIds: ids.join(','), chunked: true }),
    queryFn: async ({ signal }) => {
      const batches = chunkIds(ids, batchSize);
      let requestsDone = 0;
      let seriesDone = 0;
      setProgress({
        requestsDone: 0,
        requestsTotal: batches.length,
        seriesDone: 0,
        seriesTotal: ids.length,
      });

      const parts: RangeResponse[] = [];
      const failedSeriesIds: number[] = [];

      await inPool(batches, concurrency, signal, async (batch) => {
        try {
          parts.push(await fetchRangeBatch(batch, rest, signal));
        } catch (e) {
          if (e instanceof ApiError && e.isClientFault) throw e;
          if (signal.aborted) throw e;
          failedSeriesIds.push(...batch);
        } finally {
          requestsDone += 1;
          seriesDone += batch.length;
          setProgress({
            requestsDone,
            requestsTotal: batches.length,
            seriesDone,
            seriesTotal: ids.length,
          });
        }
      });

      // Every batch failed: there is nothing to plot, so this is an error, not a partial.
      if (parts.length === 0) throw new Error('No batch of the range query succeeded');

      return {
        response: mergeRangeResponses(parts, ids),
        requestsOk: parts.length,
        requestsTotal: batches.length,
        failedSeriesIds,
        batchSize,
        // A copy, not the caller's array: `ids` is the caller's `seriesIds` memo, which is
        // replaced on the next selection change, and a result must keep naming the question
        // it actually answered.
        requestedSeriesIds: [...ids],
      };
    },
    // Retries live per BATCH (see `fetchRangeBatch`). Retrying at this level would re-issue
    // every request in the run to recover one of them.
    retry: false,
    // Keep the previous plot and y-domain through a refetch: dropping to a skeleton every
    // 60 s reads as the chart reloading rather than as new data arriving.
    placeholderData: (prev) => prev,
    enabled: opts.enabled && ids.length > 0,
    // `exactOptionalPropertyTypes`: omit the key entirely rather than assign it `undefined`.
    ...(opts.refetchMs ? { refetchInterval: pollInterval(opts.refetchMs) } : {}),
    refetchOnWindowFocus: opts.focusRefetch,
    staleTime: opts.staleMs,
  });

  return { query, progress };
}

/* ────────────────────────────────────────────── the alarm operator axis ── */

/**
 * One alarm's workflow state, timeline, and legal next actions.
 *
 * Not polled on `LIVE_REFETCH_MS` like the alarm list. The list is live because the DEVICE
 * changes it; this changes only when a person acts, and every one of those actions returns
 * the fresh view — so a poll would refetch a value we were just handed.
 */
export function useAlarmTriage(alarmId: string | null) {
  return useQuery({
    ...base,
    queryKey: keys.alarmTriage(alarmId ?? ''),
    queryFn: () => api.get<AlarmTriage>(`/alarms/${alarmId}/triage`),
    enabled: alarmId !== null,
  });
}

/**
 * Apply a lifecycle action, add a note, or ask the platform to verify.
 *
 * Every one of these returns the whole updated `AlarmTriage`, so the cache is SEEDED from the
 * response rather than invalidated and refetched. That removes the flicker where the drawer
 * shows the old state for a beat after you press a button — and it means the timeline entry
 * the server just wrote is on screen the moment it exists.
 */
export function useAlarmAction(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      action: AlarmLifecycleAction;
      assigneeId?: string | null;
      note?: string | null;
      /** With `complete` only: what the work found (CRATUS 2.0 #9). */
      rootCause?: RootCause | null;
      /** With `complete` and a theft cause: what was taken (Lumen-BE 0088). */
      theft?: TheftInput | null;
    }) => api.post<AlarmTriage>(`/alarms/${alarmId}/actions`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      /* The lists carry the state chip, so they have to hear about this too — but not the
         triage just seeded from the response, which a prefix invalidate would refetch. */
      void qc.invalidateQueries({ queryKey: ['alarms'], predicate: notThisTriage(alarmId) });
    },
  });
}

/** Every alarm query except the one triage view a mutation has just been handed. */
const notThisTriage =
  (alarmId: string) =>
  (q: { queryKey: readonly unknown[] }): boolean =>
    !(q.queryKey[1] === 'triage' && q.queryKey[2] === alarmId);

/**
 * Record or correct what an alarm's investigation found, without moving its state
 * (`PUT /alarms/:id/root-cause`). Legal from any state.
 */
export function useAlarmRootCause(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { rootCause: RootCause; note?: string | null; theft?: TheftInput | null }) =>
      api.put<AlarmTriage>(`/alarms/${alarmId}/root-cause`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      void qc.invalidateQueries({ queryKey: ['alarms'], predicate: notThisTriage(alarmId) });
    },
  });
}

/** Record or correct what a theft attempt took, stated in full (Lumen-BE 0088). */
export function useAlarmTheft(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TheftInput) => api.put<AlarmTriage>(`/alarms/${alarmId}/theft`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      void qc.invalidateQueries({ queryKey: ['alarms'], predicate: notThisTriage(alarmId) });
    },
  });
}

export function useAlarmNote(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.post<AlarmTriage>(`/alarms/${alarmId}/notes`, { note }),
    onSuccess: (data) => qc.setQueryData(keys.alarmTriage(alarmId), data),
  });
}

export function useAlarmVerify(alarmId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AlarmTriage>(`/alarms/${alarmId}/verify`, {}),
    onSuccess: (data) => {
      qc.setQueryData(keys.alarmTriage(alarmId), data);
      void qc.invalidateQueries({ queryKey: ['alarms'], predicate: notThisTriage(alarmId) });
    },
  });
}
